import { useEffect, useState } from 'react';
import { Plus, MapPin, MoreHorizontal, Pencil, Trash2, List, Map } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toTitleCase } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
} from '@/components/common';
import {
  locationsApi,
  Location,
  LocationType,
  CreateLocationDto,
  QueryLocationParams,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with bundlers
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by type
const createIcon = (color: string) =>
  new L.DivIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });

// Leaflet renders its pins outside the CSS cascade, so these are hex rather than
// tokens. The legend is driven from the same map, so a pin and its swatch cannot
// drift apart. Supplier is slate rather than the old purple: there is no purple
// in the status palette, and an external party reads as neutral instead of
// borrowing another tone's meaning.
const markerColors: Record<string, string> = {
  HUB: '#3B82F6',
  SITE: '#10B981',
  DEPOT: '#F59E0B',
  SUPPLIER: '#64748B',
};

const markerIcons: Record<string, L.DivIcon> = Object.fromEntries(
  Object.entries(markerColors).map(([type, hex]) => [type, createIcon(hex)])
);

const locationTypeColors: Record<string, string> = {
  HUB: 'bg-status-info-subtle text-status-info-foreground',
  SITE: 'bg-status-success-subtle text-status-success-foreground',
  DEPOT: 'bg-status-warning-subtle text-status-warning-foreground',
  // No purple in the status palette; a supplier is an external party, so it
  // reads as neutral rather than borrowing another tone's meaning. Matches the
  // same map on the inventory detail page.
  SUPPLIER: 'bg-status-neutral-subtle text-status-neutral-foreground',
};

interface LocationFormData {
  name: string;
  code: string;
  type: LocationType;
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  lat: string;
  lng: string;
  isActive: boolean;
}

const emptyFormData: LocationFormData = {
  name: '',
  code: '',
  type: 'SITE',
  street: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  lat: '',
  lng: '',
  isActive: true,
};

export default function Locations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; location?: Location }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<LocationFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  const fetchLocations = async (params?: QueryLocationParams) => {
    try {
      setLoading(true);
      const response = await locationsApi.getAll(params);
      setLocations(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params: QueryLocationParams = {
      search: searchQuery || undefined,
    };
    if (typeFilter) {
      params.type = typeFilter as Location['type'];
    }
    fetchLocations(params);
  }, [searchQuery, typeFilter]);

  const handleDelete = async () => {
    if (!deleteDialog.location) return;

    try {
      setDeleting(true);
      await locationsApi.delete(deleteDialog.location.id);
      toast.success('Location deactivated successfully');
      setDeleteDialog({ open: false });
      fetchLocations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const openCreateForm = () => {
    navigate('/locations/new');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload: CreateLocationDto = {
        name: formData.name,
        code: formData.code,
        type: formData.type,
        companyId: user?.companyId || '',
        isActive: formData.isActive,
      };

      // Only include address if at least one field is filled
      if (
        formData.street ||
        formData.city ||
        formData.state ||
        formData.country ||
        formData.postalCode
      ) {
        payload.address = {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          country: formData.country || undefined,
          postalCode: formData.postalCode || undefined,
        };
      }

      // Only include contact if at least one field is filled
      if (formData.contactName || formData.contactEmail || formData.contactPhone) {
        payload.contact = {
          name: formData.contactName || undefined,
          email: formData.contactEmail || undefined,
          phone: formData.contactPhone || undefined,
        };
      }

      // Only include coordinates if both are provided
      if (formData.lat && formData.lng) {
        payload.coordinates = {
          lat: parseFloat(formData.lat),
          lng: parseFloat(formData.lng),
        };
      }

      if (editingLocation) {
        await locationsApi.update(editingLocation.id, payload);
        toast.success('Location updated successfully');
      } else {
        await locationsApi.create(payload);
        toast.success('Location created successfully');
      }

      setFormOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Location>[] = [
    {
      key: 'name',
      linkTo: (location) => `/locations/${location.id}`,
      header: 'Location',
      sortable: true,
      render: (location) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
            <MapPin className="h-4 w-4 text-accent" />
          </div>
          <div>
            <div className="font-medium">{location.name}</div>
            <div className="text-xs leading-4 text-muted-foreground">{location.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (location) => (
        <Badge className={locationTypeColors[location.type] || ''}>{location.type}</Badge>
      ),
    },
    {
      key: 'address',
      header: 'Location',
      render: (location) => (
        <span className="text-muted-foreground">
          {location.address?.city && location.address?.country
            ? `${location.address.city}, ${location.address.country}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'inventoryCount',
      header: 'Inventory Items',
      render: (location) => (
        <Badge variant="outline">{location._count?.inventoryLevels || 0}</Badge>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (location) => <StatusBadge status={location.isActive ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (location) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/locations/${location.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, location })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Locations"
        description="Hubs, sites, depots, and supplier premises, with the stock held at each."
        actions={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        }
      />

      <div className="mb-4 flex items-center justify-between">
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'map')}>
          <TabsList>
            <TabsTrigger value="list" className="gap-2">
              <List className="h-4 w-4" />
              List
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-2">
              <Map className="h-4 w-4" />
              Map
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {viewMode === 'list' ? (
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search locations..."
              filters={[
                {
                  id: 'type',
                  label: 'Types',
                  value: typeFilter,
                  options: [
                    { value: 'HUB', label: 'Hub' },
                    { value: 'SITE', label: 'Site' },
                    { value: 'DEPOT', label: 'Depot' },
                    { value: 'SUPPLIER', label: 'Supplier' },
                  ],
                  onChange: setTypeFilter,
                  placeholder: 'Type',
                },
              ]}
            />
          }
          data={locations}
          columns={columns}
          loading={loading}
          onRowClick={(location) => navigate(`/locations/${location.id}`)}
          emptyTitle="No locations found"
          emptyDescription="Get started by adding your first location."
          emptyAction={
            <Button onClick={openCreateForm}>
              <Plus className="mr-2 h-4 w-4" />
              Add Location
            </Button>
          }
          pagination
          exportable
          columnToggle
        />
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {(() => {
              const locationsWithCoords = locations.filter(
                (loc) => loc.coordinates?.lat && loc.coordinates?.lng
              );

              if (locationsWithCoords.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center h-[500px] text-center">
                    <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No locations with coordinates</p>
                    <p className="text-muted-foreground mb-4">
                      Add coordinates to your locations to see them on the map.
                    </p>
                    <Button onClick={openCreateForm}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Location
                    </Button>
                  </div>
                );
              }

              // Calculate center from all locations
              const avgLat =
                locationsWithCoords.reduce((sum, loc) => sum + (loc.coordinates?.lat || 0), 0) /
                locationsWithCoords.length;
              const avgLng =
                locationsWithCoords.reduce((sum, loc) => sum + (loc.coordinates?.lng || 0), 0) /
                locationsWithCoords.length;

              return (
                <MapContainer
                  center={[avgLat, avgLng]}
                  zoom={4}
                  style={{ height: '500px', width: '100%' }}
                  className="z-0"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {locationsWithCoords.map((location) => (
                    <Marker
                      key={location.id}
                      position={[location.coordinates!.lat, location.coordinates!.lng]}
                      icon={markerIcons[location.type] || markerIcons.SITE}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={locationTypeColors[location.type]}>
                              {location.type}
                            </Badge>
                            <StatusBadge status={location.isActive ? 'active' : 'inactive'} />
                          </div>
                          <h3 className="font-semibold text-base">{location.name}</h3>
                          <p className="text-sm text-muted-foreground">{location.code}</p>
                          {location.address?.city && (
                            <p className="text-sm mt-1">
                              {location.address.city}
                              {location.address.country && `, ${location.address.country}`}
                            </p>
                          )}
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/locations/${location.id}`)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/locations/${location.id}/edit`)}
                            >
                              Edit
                            </Button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Map Legend */}
      {viewMode === 'map' && (
        <div className="flex items-center gap-6 mt-4">
          <span className="text-sm font-medium text-muted-foreground">Legend:</span>
          {Object.entries(markerColors).map(([type, hex]) => (
            <div key={type} className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full" style={{ backgroundColor: hex }} />
              <span className="text-sm">{toTitleCase(type)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Location Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</SheetTitle>
            <SheetDescription>
              {editingLocation
                ? 'Update the location details below.'
                : 'Fill in the details to create a new location.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Houston Hub"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="HOU-001"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: LocationType) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HUB">Hub</SelectItem>
                    <SelectItem value="SITE">Site</SelectItem>
                    <SelectItem value="DEPOT">Depot</SelectItem>
                    <SelectItem value="SUPPLIER">Supplier</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.isActive ? 'Location is active' : 'Location is inactive'}
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Address</h4>
              <div className="space-y-2">
                <Label htmlFor="street">Street</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Houston"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="TX"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="77001"
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Contact</h4>
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Phone</Label>
                  <Input
                    id="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="+1 555-123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Coordinates */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Coordinates (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lat">Latitude</Label>
                  <Input
                    id="lat"
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                    placeholder="29.7604"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lng">Longitude</Label>
                  <Input
                    id="lng"
                    type="number"
                    step="any"
                    value={formData.lng}
                    onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                    placeholder="-95.3698"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingLocation ? 'Update Location' : 'Create Location'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate Location"
        description={`Are you sure you want to deactivate "${deleteDialog.location?.name}"? This location will no longer be available for new operations.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
